/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

interface LoaderProps {
  size?: "sm" | "md" | "lg";
  text?: string;
}

export function Loader({ size = "md", text }: LoaderProps) {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16"
  };

  const dotSizes = {
    sm: "w-1.5 h-1.5",
    md: "w-2 h-2",
    lg: "w-2.5 h-2.5"
  };

  return (
    <div className="flex flex-col items-center justify-center gap-6">
      {/* Geometric loader with rotating squares */}
      <div className={`${sizeClasses[size]} relative`}>
        {/* Outer rotating square */}
        <div 
          className="absolute inset-0 border-2 border-zinc-300 rounded-lg animate-[spin_3s_linear_infinite]"
          style={{ animationDirection: "reverse" }}
        />
        
        {/* Middle rotating square */}
        <div 
          className="absolute inset-2 border-2 border-zinc-400 rounded-md animate-[spin_2s_linear_infinite]"
        />
        
        {/* Inner pulsing square */}
        <div 
          className="absolute inset-4 bg-zinc-900 rounded-sm animate-[pulse_1.5s_ease-in-out_infinite]"
        />
        
        {/* Corner dots */}
        <div className="absolute -top-1 -left-1">
          <div className={`${dotSizes[size]} bg-zinc-900 rounded-full animate-[bounce_1s_ease-in-out_infinite]`} />
        </div>
        <div className="absolute -top-1 -right-1">
          <div className={`${dotSizes[size]} bg-zinc-900 rounded-full animate-[bounce_1s_ease-in-out_infinite_0.1s]`} />
        </div>
        <div className="absolute -bottom-1 -left-1">
          <div className={`${dotSizes[size]} bg-zinc-900 rounded-full animate-[bounce_1s_ease-in-out_infinite_0.2s]`} />
        </div>
        <div className="absolute -bottom-1 -right-1">
          <div className={`${dotSizes[size]} bg-zinc-900 rounded-full animate-[bounce_1s_ease-in-out_infinite_0.3s]`} />
        </div>
      </div>

      {/* Loading text with typing effect */}
      {text && (
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium text-zinc-500 tracking-wide">
            {text}
          </span>
          <span className="flex gap-0.5">
            <span className="w-1 h-1 bg-zinc-400 rounded-full animate-[bounce_0.6s_ease-in-out_infinite]" />
            <span className="w-1 h-1 bg-zinc-400 rounded-full animate-[bounce_0.6s_ease-in-out_infinite_0.1s]" />
            <span className="w-1 h-1 bg-zinc-400 rounded-full animate-[bounce_0.6s_ease-in-out_infinite_0.2s]" />
          </span>
        </div>
      )}
    </div>
  );
}

// Alternative minimal loader - morphing circle
export function LoaderMinimal({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16"
  };

  return (
    <div className={`${sizeClasses[size]} relative`}>
      <div className="absolute inset-0 border-2 border-zinc-200 rounded-full" />
      <div 
        className="absolute inset-0 border-2 border-zinc-900 rounded-full border-t-transparent animate-spin"
        style={{ animationDuration: "0.8s" }}
      />
      <div 
        className="absolute inset-2 bg-zinc-900 rounded-full animate-[pulse_1.5s_ease-in-out_infinite]"
        style={{ animationDelay: "0.2s" }}
      />
    </div>
  );
}

// Docs themed loader - document lines animation
export function LoaderDocs({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const containerSizes = {
    sm: "w-8 h-10",
    md: "w-12 h-14",
    lg: "w-16 h-20"
  };

  const lineWidths = [
    "w-full",
    "w-4/5",
    "w-3/5",
    "w-full",
    "w-2/3"
  ];

  return (
    <div className={`${containerSizes[size]} relative`}>
      {/* Document outline */}
      <div className="absolute inset-0 border-2 border-zinc-300 rounded-lg bg-white" />
      
      {/* Corner fold */}
      <div className="absolute top-0 right-0 w-4 h-4">
        <div className="absolute inset-0 bg-zinc-200 rounded-tr-lg" 
          style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }}
        />
      </div>

      {/* Animated lines */}
      <div className="absolute inset-2 flex flex-col justify-center gap-1.5">
        {lineWidths.map((width, i) => (
          <div
            key={i}
            className={`h-1 bg-zinc-900 rounded-full ${width} animate-[pulse_1s_ease-in-out_infinite]`}
            style={{ animationDelay: `${i * 0.15}s` }}
          />
        ))}
      </div>
    </div>
  );
}
